import React, { useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Tag } from 'primereact/tag';
import { FilterMatchMode } from 'primereact/api';
import { dateOnlyBodyTemplate } from '../../../../utils/dateUtils';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { REQUEST_STATUS_SEVERITY, REQUEST_STATUS_I18N_KEY, REQUEST_STATUS_OPTIONS, REQUEST_TYPE, REQUEST_TYPE_OPTIONS } from '../utils/requestStatus';

// hooks/useAllRequestsData.js'teki gruplarla birebir aynı — bkz. oradaki not. `status` ve
// `requestType` burada (mevcut TrainingRequestTable.jsx'ten farklı olarak) serbest metin değil,
// kesin eşleşme (dropdown, EQUALS) filtresi.
const EXACT_FILTER_FIELDS = ['vehicleModel', 'trainingType', 'location', 'status', 'requestType'];
const TEXT_FILTER_FIELDS = ['numberOfParticipants', 'trainingLanguage', 'createdName'];
const DATE_FILTER_FIELDS = ['preferTrainingDate', 'alternativeDate', 'createdDate'];

/**
 * "Tüm Talepler" sekmesinin listesi — `TrainingRequestTable.jsx`'in bilinçli bir kopyası/varyasyonu
 * (bkz. docs/talep_olustur_onay_akısı.md). Mevcut iki sekmenin tablosuna HİÇBİR şey eklenip
 * çıkarılmadı; bu tamamen ayrı bir bileşen. Farklar: "Talep Türü" kolonu, "Durum" kolonu artık
 * okunabilir bir Tag rozeti + dropdown filtre, ve statü geçiş/yorum geçmişi aksiyon ikonları.
 */
const AllRequestsTable = ({
    data,
    loading,
    onPage,
    lazyParams,
    totalCount,
    onEdit,
    activeFilters,
    onFiltersChange,
    vehicleModelOptions = [],
    trainingTypeOptions = [],
    trainingLocationOptions = [],
    sortField,
    sortOrder,
    onSortChange,
    onShowHistory
}) => {
    const { th } = useLanguage();

    const normalizedData = useMemo(() => {
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.items)) return data.items;
        return [];
    }, [data]);

    const textMatchModeOptions = useMemo(
        () => [
            { label: th('filter.startsWith'), value: FilterMatchMode.STARTS_WITH },
            { label: th('filter.contains'), value: FilterMatchMode.CONTAINS },
            { label: th('filter.notContains'), value: FilterMatchMode.NOT_CONTAINS },
            { label: th('filter.endsWith'), value: FilterMatchMode.ENDS_WITH },
            { label: th('filter.equals'), value: FilterMatchMode.EQUALS },
            { label: th('filter.notEquals'), value: FilterMatchMode.NOT_EQUALS }
        ],
        [th]
    );

    const statusOptions = useMemo(() => REQUEST_STATUS_OPTIONS.map((code) => ({ value: code, label: th(`trainingRequest.allRequests.statuses.${REQUEST_STATUS_I18N_KEY[code]}`) })), [th]);
    const requestTypeOptions = useMemo(
        () => REQUEST_TYPE_OPTIONS.map((code) => ({ value: code, label: code === REQUEST_TYPE.REQUESTED ? th('trainingRequest.tabs.requested') : th('trainingRequest.tabs.published') })),
        [th]
    );

    const tableFilters = useMemo(() => {
        const filters = {};
        EXACT_FILTER_FIELDS.forEach((field) => {
            filters[field] = { value: activeFilters?.[field] ?? null, matchMode: FilterMatchMode.EQUALS };
        });
        TEXT_FILTER_FIELDS.forEach((field) => {
            filters[field] = { value: activeFilters?.[field] ?? null, matchMode: activeFilters?.[`${field}MatchMode`] ?? FilterMatchMode.CONTAINS };
        });
        DATE_FILTER_FIELDS.forEach((field) => {
            filters[field] = { value: activeFilters?.[field] ?? null, matchMode: FilterMatchMode.DATE_IS };
        });
        return filters;
    }, [activeFilters]);

    const handleTableFilter = (e) => {
        const filters = e?.filters || {};
        const updated = { ...(activeFilters || {}) };

        [...EXACT_FILTER_FIELDS, ...DATE_FILTER_FIELDS].forEach((field) => {
            if (field in filters) updated[field] = filters[field]?.value ?? null;
        });
        TEXT_FILTER_FIELDS.forEach((field) => {
            if (field in filters) {
                updated[field] = filters[field]?.value ?? null;
                updated[`${field}MatchMode`] = filters[field]?.matchMode ?? FilterMatchMode.CONTAINS;
            }
        });

        onFiltersChange?.(updated);
    };

    const handleSort = (e) => {
        onSortChange?.(e.sortField, e.sortOrder);
    };

    const vehicleModelFilterTemplate = (options) => (
        <Dropdown value={options.value} options={vehicleModelOptions} optionLabel="label" optionValue="value" onChange={(e) => options.filterApplyCallback(e.value)} placeholder={th('common.search')} className="p-column-filter w-full" showClear filter style={{ width: '100%', minWidth: '10rem' }} />
    );

    const trainingTypeFilterTemplate = (options) => (
        <Dropdown value={options.value} options={trainingTypeOptions} optionLabel="label" optionValue="value" onChange={(e) => options.filterApplyCallback(e.value)} placeholder={th('common.search')} className="p-column-filter w-full" showClear filter style={{ width: '100%', minWidth: '10rem' }} />
    );

    const locationFilterTemplate = (options) => (
        <Dropdown value={options.value} options={trainingLocationOptions} optionLabel="label" optionValue="value" onChange={(e) => options.filterApplyCallback(e.value)} placeholder={th('common.search')} className="p-column-filter w-full" showClear filter style={{ width: '100%', minWidth: '10rem' }} />
    );

    const statusFilterTemplate = (options) => (
        <Dropdown value={options.value} options={statusOptions} optionLabel="label" optionValue="value" onChange={(e) => options.filterApplyCallback(e.value)} placeholder={th('common.search')} className="p-column-filter w-full" showClear style={{ width: '100%', minWidth: '10rem' }} />
    );

    const requestTypeFilterTemplate = (options) => (
        <Dropdown value={options.value} options={requestTypeOptions} optionLabel="label" optionValue="value" onChange={(e) => options.filterApplyCallback(e.value)} placeholder={th('common.search')} className="p-column-filter w-full" showClear style={{ width: '100%', minWidth: '10rem' }} />
    );

    // Tarih filtresi: warranty-claims'teki dateFilterTemplate ile aynı desen — seçilen günü
    // yerel saat diliminde 00:00:00'a normalize eder (UTC kaymasını önlemek için).
    const dateFilterTemplate = (options) => {
        const handleDateChange = (e) => {
            let normalizedDate = e.value;
            if (normalizedDate instanceof Date) {
                normalizedDate = new Date(normalizedDate.getFullYear(), normalizedDate.getMonth(), normalizedDate.getDate());
            }
            options.filterApplyCallback(normalizedDate);
        };
        return <Calendar value={options.value} onChange={handleDateChange} dateFormat="dd.mm.yy" placeholder={th('common.search')} showIcon className="p-column-filter w-full" style={{ width: '100%', minWidth: '11rem' }} inputStyle={{ width: '100%' }} />;
    };

    const locationsBodyTemplate = (rowData) => {
        if (!Array.isArray(rowData.locations) || rowData.locations.length === 0) return '';
        return rowData.locations
            .map((loc) => loc?.locationText || loc?.locationCode)
            .filter(Boolean)
            .join(', ');
    };

    const requestTypeBodyTemplate = (rowData) => (rowData.requestType === REQUEST_TYPE.REQUESTED ? th('trainingRequest.tabs.requested') : th('trainingRequest.tabs.published'));

    const statusBodyTemplate = (rowData) => {
        const i18nKey = REQUEST_STATUS_I18N_KEY[rowData.status];
        const label = i18nKey ? th(`trainingRequest.allRequests.statuses.${i18nKey}`) : rowData.status;
        return <Tag value={label} severity={REQUEST_STATUS_SEVERITY[rowData.status] || 'secondary'} />;
    };

    const actionBodyTemplate = (rowData) => (
        <div className="flex gap-2 justify-content-center flex-wrap">
            <i className="pi pi-pencil cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={th('common.edit')} onClick={() => onEdit(rowData)} />
            <i className="pi pi-clock cursor-pointer text-500 hover:text-700" style={{ fontSize: '1.1rem' }} title={th('trainingRequest.allRequests.actions.history')} onClick={() => onShowHistory?.(rowData)} />
        </div>
    );

    return (
        <DataTable
            className="p-datatable-gridlines"
            value={normalizedData}
            paginator
            rows={lazyParams.rows || 10}
            loading={loading}
            totalRecords={totalCount}
            showGridlines
            lazy
            scrollable
            scrollDirection="horizontal"
            first={lazyParams.first}
            onPage={onPage}
            filterDisplay="row"
            filters={tableFilters}
            onFilter={handleTableFilter}
            onSort={handleSort}
            sortField={sortField}
            sortOrder={sortOrder}
            dataKey="requestId"
            emptyMessage={th('trainingRequest.emptyMessage')}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            tableStyle={{ minWidth: '150rem' }}
            sortMode="single"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        >
            <Column body={actionBodyTemplate} style={{ width: '11rem' }} align="center" frozen />
            <Column
                header={th('trainingRequest.columns.requestType')}
                style={{ width: '11rem' }}
                body={requestTypeBodyTemplate}
                frozen
                filter
                filterField="requestType"
                filterElement={requestTypeFilterTemplate}
                showFilterMenu={false}
            />
            <Column
                field="status"
                header={th('trainingRequest.columns.status')}
                style={{ width: '10rem' }}
                body={statusBodyTemplate}
                filter
                filterField="status"
                filterElement={statusFilterTemplate}
                showFilterMenu={false}
            />
            <Column
                field="vehicleModel"
                header={th('trainingRequest.columns.vehicleModel')}
                style={{ width: '11rem' }}
                body={(row) => row.vehicleModelText || row.vehicleModel}
                filter
                filterField="vehicleModel"
                filterElement={vehicleModelFilterTemplate}
                showFilterMenu={false}
            />
            <Column
                field="trainingType"
                header={th('trainingRequest.columns.trainingType')}
                style={{ width: '11rem' }}
                body={(row) => row.trainingTypeText || row.trainingType}
                filter
                filterField="trainingType"
                filterElement={trainingTypeFilterTemplate}
                showFilterMenu={false}
            />
            <Column
                field="numberOfParticipants"
                header={th('trainingRequest.columns.participants')}
                style={{ width: '12rem' }}
                filter
                filterField="numberOfParticipants"
                filterPlaceholder={th('common.search')}
                showFilterMenu
                showClearButton={false}
                filterMatchModeOptions={textMatchModeOptions}
            />
            <Column
                field="preferTrainingDate"
                header={th('trainingRequest.columns.preferDate')}
                style={{ width: '11rem' }}
                body={(row) => dateOnlyBodyTemplate(row, 'preferTrainingDate')}
                sortable
                filter
                filterField="preferTrainingDate"
                filterElement={dateFilterTemplate}
                showFilterMenu={false}
            />
            <Column
                field="alternativeDate"
                header={th('trainingRequest.columns.alternativeDate')}
                style={{ width: '11rem' }}
                body={(row) => dateOnlyBodyTemplate(row, 'alternativeDate')}
                filter
                filterField="alternativeDate"
                filterElement={dateFilterTemplate}
                showFilterMenu={false}
            />
            <Column
                header={th('trainingRequest.columns.location')}
                style={{ width: '11rem' }}
                body={locationsBodyTemplate}
                filter
                filterField="location"
                filterElement={locationFilterTemplate}
                showFilterMenu={false}
            />
            <Column
                field="trainingLanguage"
                header={th('trainingRequest.columns.language')}
                style={{ width: '12rem' }}
                filter
                filterField="trainingLanguage"
                filterPlaceholder={th('common.search')}
                showFilterMenu
                showClearButton={false}
                filterMatchModeOptions={textMatchModeOptions}
            />
            <Column
                field="createdDate"
                header={th('trainingRequest.columns.createdDate')}
                style={{ width: '11rem' }}
                body={(row) => dateOnlyBodyTemplate(row, 'createdDate')}
                sortable
                filter
                filterField="createdDate"
                filterElement={dateFilterTemplate}
                showFilterMenu={false}
            />
            <Column
                field="createdName"
                header={th('trainingRequest.columns.createdName')}
                style={{ width: '12rem' }}
                filter
                filterField="createdName"
                filterPlaceholder={th('common.search')}
                showFilterMenu
                showClearButton={false}
                filterMatchModeOptions={textMatchModeOptions}
            />
        </DataTable>
    );
};

export default AllRequestsTable;
